import React, { useState, useEffect, useMemo } from "react";
import "./RichiestaFerie.css";
import { useAuthContext } from "../../hooks/useAuthContext";
import { useCollection } from "../../hooks/useCollection";
import { useDocument } from "../../hooks/useDocument";
import { useFirestore } from "../../hooks/useFirestore";
import { useEmailNotifications } from "../../hooks/useEmailNotifications";
import { database, timestamp } from "../../firebase/config";
import { format } from "date-fns";
import { it } from "date-fns/locale";

const EMAIL_RECIPIENTS_BY_SETTORE = {
  edile: ["elvis.comi@giuvi.it"],
  verona: ["stephan.kemenater@giuvi.it"],
  cucine: ["assistenza@giuvi.it"],
  intesa: ["gianni.dimaria@giuvi.it"],
  amministrazione: ["paolo@giuvi.it"],
  elettrico: ["massimo.sironi@giuvi.it", "gianfranco.calcagno@giuvi.it"],
  ftv: ["maurizio.brambilla@giuvi.it"],
};

export default function RichiestaFerie() {
  const { user } = useAuthContext();
  const { addDocument, response } = useFirestore("ferie");
  const { document: userProfile } = useDocument("users", user?.uid);
  const { sendEmail } = useEmailNotifications({
    defaultTo: ["info@giuvi.it"],
    defaultCc: ["amministrazione@giuvi.it"],
  });
  // Rimuoviamo l'ordinamento dalla query per evitare errori di indice mancante
  // Ordiniamo lato client
  const { documents: rawRichieste, error: collectionError } = useCollection(
    "ferie",
    ["userId", "==", user.uid],
  );

  const mieRichieste = useMemo(() => {
    if (!rawRichieste) return null;
    return [...rawRichieste].sort((a, b) => {
      // Gestione sicura delle date
      const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0);
      const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0);
      return dateB - dateA;
    });
  }, [rawRichieste]);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [note, setNote] = useState("");
  const [formError, setFormError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Gestione reset form al successo
  useEffect(() => {
    if (response.success) {
      setStartDate("");
      setEndDate("");
      setNote("");
      setFormError(null);
    }
  }, [response.success]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);

    if (!startDate || !endDate) {
      setFormError("Seleziona entrambe le date.");
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      setFormError(
        "La data di fine deve essere successiva alla data di inizio.",
      );
      return;
    }

    try {
      setIsSubmitting(true);

      const doc = {
        userId: user.uid,
        userName: user.displayName,
        startDate: timestamp.fromDate(new Date(startDate)),
        endDate: timestamp.fromDate(new Date(endDate)),
        note,
        status: "pending", // pending, confirmed, rejected
        createdAt: timestamp.fromDate(new Date()),
      };

      await addDocument(doc);

      const startDateFmt = format(new Date(startDate), "dd/MM/yyyy", {
        locale: it,
      });
      const endDateFmt = format(new Date(endDate), "dd/MM/yyyy", {
        locale: it,
      });

      let fullUserProfile = userProfile || null;
      if (!fullUserProfile) {
        try {
          const snap = await database.collection("users").doc(user.uid).get();
          if (snap.exists) {
            fullUserProfile = { ...snap.data(), id: snap.id };
          }
        } catch (e) {
          fullUserProfile = null;
        }
      }

      const mailRef = await sendEmail({
        to: EMAIL_RECIPIENTS_BY_SETTORE[
          String(fullUserProfile?.anagrafica?.settore || "")
            .trim()
            .toLowerCase()
        ],
        subject: "Nuova richiesta ferie",
        text: `Nuova richiesta ferie da ${user.displayName || user.email || "Utente"}\nDal: ${startDateFmt}\nAl: ${endDateFmt}\n${note ? `Note: ${note}\n` : ""}`,
        data: {
          type: "ferie_richiesta",
          userAuth: {
            uid: user.uid,
            displayName: user.displayName || "",
            email: user.email || "",
          },
          userProfile: fullUserProfile,
          richiesta: {
            startDate,
            endDate,
            startDateTs: doc.startDate,
            endDateTs: doc.endDate,
            startDateFmt,
            endDateFmt,
            note: note || "",
            status: "pending",
          },
        },
      });

      if (!mailRef) {
        throw new Error("EMAIL_DOC_NOT_CREATED");
      }
    } catch (err) {
      setFormError(
        "Richiesta inviata, ma notifica email non creata correttamente.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="richiesta-ferie-container">
      <h1>Richiesta Ferie</h1>

      <div className="richiesta-form-card">
        <h2>Nuova Richiesta</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Data Inizio</label>
              <input
                type="date"
                required
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Data Fine</label>
              <input
                type="date"
                required
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Note (Opzionale)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
            />
          </div>

          {formError && <div className="error-msg">{formError}</div>}
          {response.error && (
            <div className="error-msg">Errore invio: {response.error}</div>
          )}

          <button className="btn-submit" disabled={isSubmitting}>
            {isSubmitting ? "Invio in corso..." : "Invia Richiesta"}
          </button>
        </form>
      </div>

      <div className="richieste-list">
        <h2>Le tue richieste</h2>
        {collectionError && (
          <div className="error-msg">Errore caricamento: {collectionError}</div>
        )}

        {mieRichieste && mieRichieste.length === 0 && (
          <p>Non hai ancora inviato richieste.</p>
        )}

        <div className="cards-grid">
          {mieRichieste &&
            mieRichieste.map((req) => (
              <div
                key={req.id}
                className={`richiesta-card status-${req.status}`}
              >
                <div className="card-header">
                  <span className="status-badge">{req.status}</span>
                  <span className="date-created">
                    Inviata il:{" "}
                    {req.createdAt?.toDate
                      ? format(req.createdAt.toDate(), "dd/MM/yyyy", {
                          locale: it,
                        })
                      : "-"}
                  </span>
                </div>
                <div className="card-body">
                  <p>
                    <strong>Dal:</strong>{" "}
                    {req.startDate?.toDate
                      ? format(req.startDate.toDate(), "dd/MM/yyyy", {
                          locale: it,
                        })
                      : "-"}
                  </p>
                  <p>
                    <strong>Al:</strong>{" "}
                    {req.endDate?.toDate
                      ? format(req.endDate.toDate(), "dd/MM/yyyy", {
                          locale: it,
                        })
                      : "-"}
                  </p>
                  {req.note && <p className="req-note">Note: {req.note}</p>}
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
